import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/shared/Logo';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import api from '../services/api';
import './ProfileScreen.css';

export const ProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    idNumber: '',
    shop2shopAccount: '',
    shop2shopUserRef: '',
    kazangAccount: '',
  });

  useEffect(() => {
    if ((location.state as any)?.startEditing) {
      setIsEditing(true);
    }
  }, [location.state]);

  useEffect(() => {
    const loadUserProfile = async () => {
      setLoading(true);
      setError('');

      try {
        const profile = await api.users.getProfile();
        
        setUserData({
          name: profile.name || '',
          email: profile.email || '',
          phone: profile.phone || '',
          dateOfBirth: profile.dateOfBirth || '',
          address: profile.address || '',
          idNumber: profile.idNumber || '',
          shop2shopAccount: profile.shop2shopAccount || '',
          shop2shopUserRef: profile.shop2shopUserRef || '',
          kazangAccount: profile.kazangAccount || '',
        });

      } catch (err: any) {
        console.error('Failed to load user profile:', err);
        setError('Failed to load profile data. Please try again.');

        // Fallback to user data from auth context if available
        if (user) {
          setUserData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            dateOfBirth: '',
            address: '',
            idNumber: '',
            shop2shopAccount: '',
            shop2shopUserRef: '',
            kazangAccount: '',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      // Send all profile fields that are now available in the database
      // Filter out empty strings to avoid issues
      const updateData: any = {};
      
      // Always include name as it's required
      updateData.name = userData.name?.trim() || '';
      
      // Include other fields only if they have values
      if (userData.email?.trim()) updateData.email = userData.email.trim();
      if (userData.phone?.trim()) updateData.phone = userData.phone.trim();
      if (userData.dateOfBirth?.trim()) updateData.dateOfBirth = userData.dateOfBirth.trim();
      if (userData.address?.trim()) updateData.address = userData.address.trim();
      if (userData.idNumber?.trim()) updateData.idNumber = userData.idNumber.trim();
      if (userData.shop2shopAccount?.trim()) updateData.shop2shopAccount = userData.shop2shopAccount.trim();
      if (userData.kazangAccount?.trim()) updateData.kazangAccount = userData.kazangAccount.trim();

      console.log('Sending update data:', updateData);

      const updatedUser = await api.users.updateProfile(updateData);

      // Update local state from server response.
      setUserData(prevData => ({
        ...prevData,
        name: updatedUser.name || prevData.name,
        email: updatedUser.email || prevData.email,
        phone: updatedUser.phone || prevData.phone,
        dateOfBirth: updatedUser.dateOfBirth || prevData.dateOfBirth,
        address: updatedUser.address || prevData.address,
        idNumber: updatedUser.idNumber || prevData.idNumber,
        shop2shopAccount: updatedUser.shop2shopAccount || prevData.shop2shopAccount,
        shop2shopUserRef: updatedUser.shop2shopUserRef || prevData.shop2shopUserRef,
        kazangAccount: updatedUser.kazangAccount || prevData.kazangAccount,
      }));

      setIsEditing(false);
      // Show success message
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Invalid profile data.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof userData, value: string) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="profile-screen">
        <LoadingSpinner message="Loading your profile..." />
      </div>
    );
  }

  return (
    <div className="profile-screen">
      <div className="profile-container">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>My Profile</h1>
          </div>
          <div className="header-right">
            <button 
              onClick={() => setIsEditing(!isEditing)} 
              className="edit-button"
            >
              <i className={isEditing ? "fas fa-times" : "fas fa-edit"}></i>
            </button>
            <Logo size="small" variant="light" />
          </div>
        </div>

        <div className="screen-content">
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Profile Header */}
          <div className="profile-header">
            <div className="profile-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="profile-basic-info">
              {isEditing ? (
                <input
                  type="text"
                  value={userData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="edit-input name-input"
                  placeholder="Full Name"
                />
              ) : (
                <h2>{userData.name || 'No name provided'}</h2>
              )}
              <p>Yebo Fresh Member</p>
            </div>
          </div>

          {/* Profile Details */}
          <div className="profile-details">
            <div className="details-section">
              <h3>Personal Information</h3>
              
              <div className="detail-item">
                <label>Email Address</label>
                {isEditing ? (
                  <input
                    type="email"
                    value={userData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <span>{userData.email || 'Not specified'}</span>
                )}
              </div>

              <div className="detail-item">
                <label>Phone Number</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={userData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <span>{userData.phone || 'Not specified'}</span>
                )}
              </div>

              <div className="detail-item">
                <label>Date of Birth</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={userData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <span>{formatDate(userData.dateOfBirth)}</span>
                )}
              </div>

              <div className="detail-item">
                <label>ID Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={userData.idNumber}
                    onChange={(e) => {
                      // Only allow digits and limit to 13 characters for South African ID
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 13) {
                        handleInputChange('idNumber', value);
                      }
                    }}
                    className="edit-input"
                    placeholder="ID Number (13 digits)"
                    maxLength={13}
                  />
                ) : (
                  <span>{userData.idNumber ? userData.idNumber.replace(/(\d{6})(\d{7})/, '$1 *** $2') : 'Not specified'}</span>
                )}
              </div>
            </div>

            <div className="details-section">
              <h3>Address Information</h3>

              <div className="detail-item">
                <label>Address</label>
                {isEditing ? (
                  <textarea
                    value={userData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="edit-textarea"
                    rows={3}
                    placeholder="Full address"
                  />
                ) : (
                  <span>{userData.address || 'Not specified'}</span>
                )}
              </div>
            </div>

            <div className="details-section">
              <h3>Linked Payment Accounts</h3>

              <div className="detail-item">
                <label>Shop2Shop Shop Code</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={userData.shop2shopAccount}
                    onChange={(e) => handleInputChange('shop2shopAccount', e.target.value.toUpperCase())}
                    className="edit-input"
                    placeholder="e.g. EX9QK9"
                    maxLength={32}
                  />
                ) : (
                  <span>{userData.shop2shopAccount || 'Not linked'}</span>
                )}
              </div>

              {userData.shop2shopUserRef && (
                <div className="detail-item ref-display">
                  <label>Your Yebo reference</label>
                  <div className="ref-row">
                    <span className="ref-value">{userData.shop2shopUserRef}</span>
                    <button
                      type="button"
                      className="ref-copy"
                      onClick={() => navigator.clipboard.writeText(userData.shop2shopUserRef)}
                      aria-label="Copy reference"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </div>
              )}

              <div className="detail-item">
                <label>Kazang account</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={userData.kazangAccount}
                    onChange={(e) => handleInputChange('kazangAccount', e.target.value)}
                    className="edit-input"
                    placeholder="Phone or account number"
                  />
                ) : (
                  <span>{userData.kazangAccount || 'Not linked'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing ? (
            <div className="profile-actions">
              <button 
                onClick={handleSave} 
                className="save-button"
                disabled={loading}
              >
                <i className={loading ? "fas fa-spinner fa-spin" : "fas fa-check"}></i>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button 
                onClick={() => setIsEditing(false)} 
                className="cancel-button"
                disabled={loading}
              >
                <i className="fas fa-times"></i>
                Cancel
              </button>
            </div>
          ) : (
            <div className="profile-actions">
              <button className="settings-button">
                <i className="fas fa-cog"></i>
                Settings
              </button>
              <button className="security-button">
                <i className="fas fa-shield-alt"></i>
                Security
              </button>
              <button className="help-button">
                <i className="fas fa-question-circle"></i>
                Help & Support
              </button>
              <button onClick={handleLogout} className="logout-button">
                <i className="fas fa-sign-out-alt"></i>
                Logout
              </button>
            </div>
          )}

          {/* App Info */}
          <div className="app-info">
            <p>Yebo Fresh Wallet v1.0.0</p>
            <p>© 2024 Yebo Fresh. All rights reserved.</p>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};